import React, { useState } from 'react';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { CreateRequeteDialog } from './CreateRequeteDialog';
import { DemoRequestInfos } from './requetes/DemoRequestInfos';
import { DemoRequestMessages } from './requetes/DemoRequestMessages';
import { BROSSARD_DEMO_REQUESTS, DEMO_CITIZEN, type DemoRequestDetail } from '../data/brossardRequests';
import { useLanguage } from '../contexts/LanguageContext';
import { SearchIcon, FilterIcon, MapPinIcon, MapIcon, TableIcon, FolderIcon, ChevronLeftIcon, ChevronRightIcon, ChevronsUpDownIcon, PlusIcon, SettingsIcon, XIcon, PaperclipIcon, CheckSquareIcon, SquareIcon, LinkIcon, TagIcon, FileTextIcon, PrinterIcon, SendIcon, LockIcon, BellIcon, CheckIcon } from 'lucide-react';
type ViewMode = 'list' | 'map';
/** Citizen-facing tabs on the left pane. */
type LeftTab = 'infos' | 'messages';
/** Internal tabs on the right pane. */
type RightTab = 'gestion' | 'collaboration' | 'historique';
type Status = 'Nouvelle demande' | 'En Cours' | 'Complété' | 'En Attente' | 'Reçue';
interface Request {
  id: string;
  subject: string;
  category: string;
  reqId: string;
  location: string;
  date: string;
  status: Status;
  department: string;
  requester: {
    name: string;
    initials: string;
    color: string;
  };
  slaDate: string;
  /** Brossard demo requests carry their full file (infos, history, messages). */
  demo?: DemoRequestDetail;
}
/** The four Brossard demo requests, shown first. */
const DEMO_REQUESTS: Request[] = BROSSARD_DEMO_REQUESTS.map((r, i) => ({
  id: `demo-${i}`,
  subject: r.title.fr,
  category: r.category.fr,
  reqId: `#${r.reqId}`,
  location: r.addressLines[0],
  date: r.createdOn.fr,
  status: r.reqId === 'REQ-2790' ? 'En Cours' : 'Reçue',
  department: r.service.fr,
  requester: {
    name: DEMO_CITIZEN.name,
    initials: DEMO_CITIZEN.initials,
    color: 'bg-[#4F46E5]'
  },
  slaDate: r.dueOn.fr,
  demo: r
}));
const MOCK_REQUESTS: Request[] = [...DEMO_REQUESTS, {
  id: '1',
  subject: 'Divers',
  category: 'Voirie et infrastructures routi...',
  reqId: 'R-101',
  location: '1520, 1 ère Rue, Richelieu,...',
  date: '8 juin 2026',
  status: 'Nouvelle demande',
  department: 'Service Des Travaux Publics',
  requester: {
    name: 'Nathalie Ouellet',
    initials: 'NO',
    color: 'bg-[#4F46E5]'
  },
  slaDate: '13 juin 2026'
}, {
  id: '2',
  subject: 'Bâtiment',
  category: 'Bâtiment',
  reqId: 'R-100',
  location: '205, Avenue 8e, Richelie...',
  date: '8 juin 2026',
  status: 'Nouvelle demande',
  department: 'Service Des Travaux Publics',
  requester: {
    name: 'Nathalie Ouellet',
    initials: 'NO',
    color: 'bg-[#4F46E5]'
  },
  slaDate: '8 juillet 2026'
}, {
  id: '3',
  subject: 'Nid-de-poule - trait...',
  category: 'Voirie et infrastructures routi...',
  reqId: 'R-99',
  location: '2907, Rang de la Savane, ...',
  date: '8 juin 2026',
  status: 'Nouvelle demande',
  department: 'Service Des Travaux Publics',
  requester: {
    name: 'Caroline Nadeau',
    initials: 'CN',
    color: 'bg-[#4F46E5]'
  },
  slaDate: '13 juin 2026'
}, {
  id: '4',
  subject: 'Divers',
  category: 'Voirie et infrastructures routi...',
  reqId: 'R-98',
  location: '32, Place Rouville-Tessier,...',
  date: '8 juin 2026',
  status: 'Complété',
  department: 'Service Des Travaux Publics',
  requester: {
    name: 'Nathalie Ouellet',
    initials: 'NO',
    color: 'bg-[#4F46E5]'
  },
  slaDate: '13 juin 2026'
}, {
  id: '5',
  subject: 'Divers',
  category: 'Voirie et infrastructures routi...',
  reqId: 'R-97',
  location: '700, Rue Martel, Richelie...',
  date: '4 juin 2026',
  status: 'En Cours',
  department: 'Service Des Travaux Publics',
  requester: {
    name: 'Nathalie Ouellet',
    initials: 'NO',
    color: 'bg-[#4F46E5]'
  },
  slaDate: '9 juin 2026'
}, {
  id: '6',
  subject: 'Plateaux sportifs - t...',
  category: 'Parcs et espaces récréatifs',
  reqId: 'R-96',
  location: '900, Rue des Oblats, Ric...',
  date: '3 juin 2026',
  status: 'Complété',
  department: 'Service Des Travaux Publics',
  requester: {
    name: 'Nathalie Ouellet',
    initials: 'NO',
    color: 'bg-[#4F46E5]'
  },
  slaDate: '8 juin 2026'
}, {
  id: '7',
  subject: 'Refoulement égout...',
  category: 'Eau potable et égout',
  reqId: 'R-95',
  location: '192, Rue des Érables, Ric...',
  date: '3 juin 2026',
  status: 'Nouvelle demande',
  department: 'Service Des Travaux Publics',
  requester: {
    name: 'Éric Robin',
    initials: 'ÉR',
    color: 'bg-[#4F46E5]'
  },
  slaDate: '4 juin 2026'
}];
/** English equivalents for the French mock data and status labels. */
const EN_DICT: Record<string, string> = {
  // Brossard demo requests
  ...Object.fromEntries(BROSSARD_DEMO_REQUESTS.flatMap(r => [[r.title.fr, r.title.en], [r.category.fr, r.category.en], [r.service.fr, r.service.en], [r.createdOn.fr, r.createdOn.en], [r.dueOn.fr, r.dueOn.en]])),
  // Statuses
  'Nouvelle demande': 'New request',
  'En Cours': 'In progress',
  'Complété': 'Completed',
  'En Attente': 'On hold',
  'Reçue': 'Received',
  // Subjects & categories
  Divers: 'Miscellaneous',
  Bâtiment: 'Building',
  'Nid-de-poule - trait...': 'Pothole - treat...',
  'Plateaux sportifs - t...': 'Sports facilities - t...',
  'Refoulement égout...': 'Sewer backup...',
  'Voirie et infrastructures routi...': 'Roads and road infrastruc...',
  'Parcs et espaces récréatifs': 'Parks and recreational spaces',
  'Eau potable et égout': 'Drinking water and sewer',
  Taxation: 'Taxation',
  Administration: 'Administration',
  'Lampadaire défectueux': 'Faulty streetlight',
  'Éclairage public': 'Street lighting',
  'Eau potable (qualité)': 'Drinking water (quality)',
  'Feux de circulation': 'Traffic lights',
  // Departments
  'Service Des Travaux Publics': 'Public Works Department',
  // Dates
  '8 juin 2026': 'June 8, 2026',
  '4 juin 2026': 'June 4, 2026',
  '3 juin 2026': 'June 3, 2026',
  '13 juin 2026': 'June 13, 2026',
  '9 juin 2026': 'June 9, 2026',
  '4 juin 2026 ': 'June 4, 2026',
  '8 juillet 2026': 'July 8, 2026'
};
const getStatusColor = (status: Status) => {
  switch (status) {
    case 'Nouvelle demande':
      return 'bg-slate-200 text-slate-700';
    case 'En Cours':
      return 'bg-indigo-100 text-indigo-700';
    case 'Complété':
      return 'bg-indigo-100 text-indigo-700';
    case 'En Attente':
      return 'bg-orange-100 text-orange-700';
    case 'Reçue':
      return 'bg-slate-100 text-slate-600';
    default:
      return 'bg-slate-100 text-slate-700';
  }
};
const getStatusDot = (status: Status) => {
  switch (status) {
    case 'Nouvelle demande':
      return 'bg-slate-400';
    case 'En Cours':
      return 'bg-[#4F46E5]';
    case 'Complété':
      return 'bg-[#4F46E5]';
    case 'En Attente':
      return 'bg-orange-500';
    case 'Reçue':
      return 'bg-slate-400';
    default:
      return 'bg-slate-400';
  }
};
// Request locations around Windsor, QC
const MAP_MARKERS: {
  reqId: string;
  subject: string;
  category: string;
  position: [number, number];
}[] = [...BROSSARD_DEMO_REQUESTS.map(r => ({
  reqId: `#${r.reqId}`,
  subject: r.title.fr,
  category: r.category.fr,
  position: r.coords
})), {
  reqId: 'R-127',
  subject: 'Taxation',
  category: 'Administration',
  position: [45.4528, -73.4622]
}, {
  reqId: 'R-104',
  subject: 'Lampadaire défectueux',
  category: 'Éclairage public',
  position: [45.4468, -73.4698]
}, {
  reqId: 'R-70',
  subject: 'Eau potable (qualité)',
  category: 'Eau potable et égout',
  position: [45.4552, -73.4578]
}, {
  reqId: 'R-103',
  subject: 'Feux de circulation',
  category: 'Éclairage public',
  position: [45.4438, -73.4728]
}, {
  reqId: 'R-57',
  subject: 'Lampadaire défectueux',
  category: 'Éclairage public',
  position: [45.4488, -73.4548]
}];
// Custom indigo map marker
const requestMarkerIcon = L.divIcon({
  className: '',
  html: `<div style="display:flex;align-items:center;justify-content:center;width:28px;height:28px;border-radius:9999px;background:#4F46E5;box-shadow:0 2px 6px rgba(0,0,0,0.3);border:2px solid white;"><svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13c0 1.1.9 2 2 2Z"/></svg></div>`,
  iconSize: [28, 28],
  iconAnchor: [14, 14],
  popupAnchor: [0, -14]
});
export function RequetesGerer() {
  const {
    t,
    lang
  } = useLanguage();
  /** Translates a French mock value to English when the interface is in English. */
  const tr = (s: string) => lang === 'en' ? EN_DICT[s] ?? s : s;
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [selectedRequest, setSelectedRequest] = useState<Request | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [leftTab, setLeftTab] = useState<LeftTab>('infos');
  const [rightTab, setRightTab] = useState<RightTab>('gestion');
  const [draft, setDraft] = useState('');
  const [noteDraft, setNoteDraft] = useState('');
  if (selectedRequest) {
    const demo = selectedRequest.demo;
    return <div className="bg-slate-50 -mx-4 sm:-mx-6 lg:-mx-8 -my-8 min-h-screen flex" data-id="element-5760" data-mp="33lwrvtnuir6r">
        {/* Left Pane - Citizen Visible */}
        <div className="w-1/2 border-r border-slate-200 bg-white flex flex-col" data-id="element-5761" data-mp="2ykh968954wrj">
          <div className="p-4 border-b border-slate-200 flex items-center gap-2 text-slate-500 text-sm font-medium" data-id="element-5762" data-mp="itblqb5sveyl">
            <div className="w-4 h-4 rounded-full border-2 border-slate-300 flex items-center justify-center" data-id="element-5763" data-mp="dv9wt9lp0lf7">
              <div className="w-1.5 h-1.5 bg-slate-300 rounded-full" data-id="element-5764" data-mp="307bmkjm95mq4"></div>
            </div>
            {t('Visible par le citoyen', 'Visible to the citizen')}
          </div>

          <div className="px-6 pt-4 border-b border-slate-200 flex gap-6" data-id="element-5765" data-mp="2qe4kp8qq0ous">
            <button onClick={() => setLeftTab('infos')} className={`pb-3 border-b-2 font-semibold text-sm transition-colors ${leftTab === 'infos' ? 'border-[#4F46E5] text-[#4F46E5]' : 'border-transparent text-slate-500 hover:text-slate-700'}`} data-id="element-5766" data-mp="2lfkyd787tb1w">
              {t('Infos', 'Details')}
            </button>
            <button onClick={() => setLeftTab('messages')} className={`pb-3 border-b-2 font-semibold text-sm transition-colors flex items-center gap-2 ${leftTab === 'messages' ? 'border-[#4F46E5] text-[#4F46E5]' : 'border-transparent text-slate-500 hover:text-slate-700'}`} data-id="element-5767" data-mp="22j1ryposq191">
              {t('Messages', 'Messages')}
              {demo ? demo.messages.length > 0 && <span className="px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500 text-[10px] font-bold" data-id="element-5768" data-mp="i07tlo51c0x0">
                    {demo.messages.length}
                  </span> : <span className="px-1.5 py-0.5 rounded-full bg-[#4F46E5] text-white text-[10px] font-bold" data-id="element-5769" data-mp="3li9a0lokijys">
                  1
                </span>}
            </button>
          </div>

          {leftTab === 'messages' && <>
              {demo ? <div className="p-6 overflow-y-auto flex-1" data-id="element-5770" data-mp="49vz5ikz9q2p">
                  <DemoRequestMessages messages={demo.messages} data-id="element-5771" data-mp="1aowp97nbne1d" />
                </div> : <div className="p-6 overflow-y-auto flex-1 space-y-5" data-id="element-5772" data-mp="1fmonskih1yej">
                <div className="flex items-center gap-2 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2" data-id="element-5773" data-mp="3bqmcmainhru1">
                  <BellIcon className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" data-id="element-5774" data-mp="36qgjepwqfx9u" />
                  {t('Nathalie Ouellet reçoit chaque message par courriel et notification bciti+.', 'Nathalie Ouellet receives every message by email and bciti+ notification.')}
                </div>

                <div className="text-center" data-id="element-5775" data-mp="2sxquodptke91">
                  <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider" data-id="element-5776" data-mp="2o1tgacy49hyw">
                    {t('19 mai 2026', 'May 19, 2026')}
                  </span>
                </div>

                {/* Citizen message */}
                <div className="flex gap-3" data-id="element-5777" data-mp="2wzlvzi7r2rmt">
                  <div className="w-8 h-8 rounded-full bg-[#4F46E5] text-white flex items-center justify-center text-xs font-bold flex-shrink-0" data-id="element-5778" data-mp="29bce2rdnqo0y">
                    NO
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-5779" data-mp="1v629x3itpq3q">
                    <div className="bg-slate-50 border border-slate-200 rounded-xl rounded-tl-sm p-3.5 text-sm text-slate-700 leading-relaxed" data-id="element-5780" data-mp="1v5amwh3al0p2">
                      {t("Bonjour, je voulais savoir si quelqu'un est passé au parc. Les branches sont toujours sur le sentier ce matin et les enfants passent par là pour aller à l'école.", 'Hello, I wanted to know if someone came by the park. The branches are still on the path this morning and the children walk through there to get to school.')}
                    </div>
                    <div className="text-[11px] text-slate-400 mt-1" data-id="element-5781" data-mp="23ynsaiijlus1">
                      Nathalie Ouellet · {t('19 mai, 08:12', 'May 19, 08:12')}
                    </div>
                  </div>
                </div>

                {/* Staff reply */}
                <div className="flex gap-3 flex-row-reverse" data-id="element-5782" data-mp="xjd4ejowwvd4">
                  <div className="w-8 h-8 rounded-full bg-slate-800 text-white flex items-center justify-center text-[10px] font-bold flex-shrink-0" data-id="element-5783" data-mp="xgh4ipti7wm1">
                    JD
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-5784" data-mp="18xeznyhityh8">
                    <div className="bg-[#4F46E5] rounded-xl rounded-tr-sm p-3.5 text-sm text-white leading-relaxed" data-id="element-5785" data-mp="14182ov73rxbf">
                      {t('Bonjour Mme Ouellet, merci pour votre signalement. Une équipe des Travaux publics est planifiée pour le ramassage des branches le 21 mai en avant-midi. Les poubelles ont été vidées hier.', 'Hello Ms. Ouellet, thank you for your report. A Public Works crew is scheduled to pick up the branches on the morning of May 21. The garbage bins were emptied yesterday.')}
                    </div>
                    <div className="text-[11px] text-slate-400 mt-1 text-right flex items-center justify-end gap-1.5" data-id="element-5786" data-mp="bcalywiji8ks">
                      Jacques Dubois · {t('19 mai, 09:47', 'May 19, 09:47')}
                      <CheckIcon className="w-3 h-3 text-emerald-500" data-id="element-5787" data-mp="2i0t6cefb8tqs" />
                      <span className="text-emerald-600 font-medium" data-id="element-5788" data-mp="2mybei2li74xq">{t('Lu', 'Read')}</span>
                    </div>
                  </div>
                </div>

                <div className="text-center" data-id="element-5789" data-mp="2rvutx5txtnr9">
                  <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider" data-id="element-5790" data-mp="2woxnvf2mmz7z">
                    {t('20 mai 2026', 'May 20, 2026')}
                  </span>
                </div>

                {/* Automated status message */}
                <div className="flex gap-3" data-id="element-5791" data-mp="35rf8jp2mvjsv">
                  <div className="w-8 h-8 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center flex-shrink-0" data-id="element-5792" data-mp="3ao5rql9qivii">
                    <BellIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-5793" data-mp="3fmqoubtowz2q" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-5794" data-mp="1gc8uucsiwxg2">
                    <div className="bg-white border border-dashed border-slate-300 rounded-xl p-3.5 text-sm text-slate-600 leading-relaxed" data-id="element-5795" data-mp="1l8za4xwi7p0f">
                      <span className="font-semibold text-slate-700" data-id="element-5796" data-mp="1d9bbjmqtv20i">
                        {t('Mise à jour automatique', 'Automatic update')}
                      </span>
                      <br data-id="element-5797" data-mp="1n80kur4nre55" />
                      {t('Le statut de votre requête est passé à « En cours ». Échéance prévue : 26 mai 2026.', 'Your request status changed to “In progress”. Expected due date: May 26, 2026.')}
                    </div>
                    <div className="text-[11px] text-slate-400 mt-1" data-id="element-5798" data-mp="1s5k9mkyzf9bc">
                      {t('Envoyé automatiquement · 20 mai, 07:00', 'Sent automatically · May 20, 07:00')}
                    </div>
                  </div>
                </div>

                {/* Latest citizen message, unanswered */}
                <div className="flex gap-3" data-id="element-5799" data-mp="2fvwhjvu8961x">
                  <div className="w-8 h-8 rounded-full bg-[#4F46E5] text-white flex items-center justify-center text-xs font-bold flex-shrink-0" data-id="element-5800" data-mp="2ax3dsv4qstey">
                    NO
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-5801" data-mp="2yjoru5ro6p2o">
                    <div className="bg-slate-50 border border-slate-200 rounded-xl rounded-tl-sm p-3.5 text-sm text-slate-700 leading-relaxed" data-id="element-5802" data-mp="2yjf3snq6qrzm">
                      {t('Parfait, merci beaucoup pour le suivi rapide !', 'Perfect, thank you very much for the quick follow-up!')}
                    </div>
                    <div className="text-[11px] text-slate-400 mt-1" data-id="element-5803" data-mp="2fm4e9uucvcxd">
                      Nathalie Ouellet · {t('20 mai, 11:23', 'May 20, 11:23')} ·{' '}
                      <span className="text-[#4F46E5] font-medium" data-id="element-5804" data-mp="26ssgxap66ui1">
                        {t('Sans réponse', 'Unanswered')}
                      </span>
                    </div>
                  </div>
                </div>
              </div>}

              {/* Composer */}
              <div className="border-t border-slate-200 p-4 bg-white" data-id="element-5805" data-mp="1wwfmubppqemr">
                <textarea value={draft} onChange={e => setDraft(e.target.value)} rows={3} placeholder={t('Écrire au citoyen...', 'Write to the citizen...')} className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:border-transparent" data-id="element-5806" data-mp="2cc2xpjm8qa4r" />
                <div className="flex items-center justify-between mt-2.5" data-id="element-5807" data-mp="1on1tk8kfxl5a">
                  <div className="flex items-center gap-1" data-id="element-5808" data-mp="1olqqkaytkhf8">
                    <button aria-label={t('Joindre un fichier', 'Attach a file')} className="p-2 rounded-lg text-slate-400 hover:text-[#4F46E5] hover:bg-indigo-50 transition-colors" data-id="element-5809" data-mp="1kkn7zgpe07ab">
                      <PaperclipIcon className="w-4 h-4" data-id="element-5810" data-mp="1apdb17duiq3e" />
                    </button>
                    <span className="text-xs text-slate-400" data-id="element-5811" data-mp="15oxu65ucv0go">
                      {t('Visible par le citoyen', 'Visible to the citizen')}
                    </span>
                  </div>
                  <button disabled={!draft.trim()} onClick={() => setDraft('')} className="inline-flex items-center gap-2 px-4 py-2 bg-[#4F46E5] text-white text-sm font-semibold rounded-lg hover:bg-[#4338CA] disabled:opacity-40 disabled:cursor-not-allowed transition-colors" data-id="element-5812" data-mp="10hr6v0zkab4x">
                    <SendIcon className="w-4 h-4" data-id="element-5813" data-mp="hsb384sky2ka" />
                    {t('Envoyer', 'Send')}
                  </button>
                </div>
              </div>
            </>}

          <div className={`p-6 overflow-y-auto flex-1 ${leftTab === 'infos' ? '' : 'hidden'}`} data-id="element-5814" data-mp="ridthx8bh2ex">
            {demo ? <DemoRequestInfos request={demo} statusBadge={<div className={`px-3 py-1 rounded-full text-sm font-medium flex items-center gap-2 flex-shrink-0 ${getStatusColor(selectedRequest.status)}`} data-id="element-5816" data-mp="3v6frmotnbegx">
                    <div className={`w-2 h-2 rounded-full ${getStatusDot(selectedRequest.status)}`} data-id="element-5817" data-mp="1w8h3rrhk1rk3"></div>
                    {tr(selectedRequest.status)}
                  </div>} data-id="element-5815" data-mp="3q7uoagj3keo9" /> : <>
            <div className="flex justify-between items-start mb-8" data-id="element-5818" data-mp="25845kee74zdo">
              <div className="flex gap-4" data-id="element-5819" data-mp="18lr2wtgy0312">
                <div className="w-12 h-12 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-center flex-shrink-0" data-id="element-5820" data-mp="1dj13n59s8147">
                  <FolderIcon className="w-6 h-6 text-[#4F46E5]" data-id="element-5821" data-mp="1hk2tivht70ti" />
                </div>
                <div data-id="element-5822" data-mp="1mcmi0ni1bgd3">
                  <div className="flex items-center gap-2" data-id="element-5823" data-mp="1md5on828iibt">
                    <h2 className="text-xl font-bold text-slate-900" data-id="element-5824" data-mp="pu812pynmv0m">
                      {t('Entretien des parcs', 'Park maintenance')}
                    </h2>
                    <ChevronLeftIcon className="w-4 h-4 text-slate-400 rotate-[-90deg]" data-id="element-5825" data-mp="tsxdogbvc8j7" />
                  </div>
                  <div className="text-sm text-slate-500 mt-1 flex items-center gap-2" data-id="element-5826" data-mp="hco8d5088ngr">
                    <span className="bg-slate-100 px-2 py-0.5 rounded text-xs font-medium border border-slate-200" data-id="element-5827" data-mp="hav3nv96cdbe">
                      R-71
                    </span>
                    <span data-id="element-5828" data-mp="8bhtf0d87kpm">{tr('Parcs et espaces récréatifs')}</span>
                  </div>
                  <div className="text-sm text-slate-400 mt-1 ml-10 border-l-2 border-slate-200 pl-2" data-id="element-5829" data-mp="3ehjufh63ztr">
                    {tr('Service Des Travaux Publics')}
                  </div>
                </div>
              </div>
              <div className={`px-3 py-1 rounded-full text-sm font-medium flex items-center gap-2 ${getStatusColor('En Cours')}`} data-id="element-5830" data-mp="3pqesmi2tb9kn">
                <div className={`w-2 h-2 rounded-full ${getStatusDot('En Cours')}`} data-id="element-5831" data-mp="3gqrr7wa323rl"></div>
                {tr('En Cours')}
              </div>
            </div>

            <div className="mb-8" data-id="element-5832" data-mp="3242ozmog5chi">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3" data-id="element-5833" data-mp="32772xbrprmld">
                {t('DESCRIPTION', 'DESCRIPTION')}
              </h3>
              <div className="p-4 rounded-xl border border-slate-200 bg-white text-slate-700 text-sm leading-relaxed" data-id="element-5834" data-mp="2s7zosmd5nnx7">
                {t("Le parc près de la rue Principale nécessite un entretien urgent. Les poubelles débordent et il y a des branches cassées sur le sentier principal suite à la tempête d'hier soir. Cela représente un danger pour les piétons et les cyclistes.", "The park near Principale Street needs urgent maintenance. The garbage bins are overflowing and there are broken branches on the main path following last night's storm. This is a hazard for pedestrians and cyclists.")}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-8 mb-8" data-id="element-5835" data-mp="cg213159vk7n">
              <div data-id="element-5836" data-mp="chn0p56g1tcd">
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3" data-id="element-5837" data-mp="1x5xnxp0tbdvt">
                  {t('DEMANDEUR', 'REQUESTER')}
                </h3>
                <div className="flex items-start gap-3" data-id="element-5838" data-mp="1s508vlb3mzyj">
                  <div className="w-8 h-8 rounded-full bg-[#4F46E5] text-white flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5" data-id="element-5839" data-mp="26uz6nmyqs9dh">
                    NO
                  </div>
                  <div data-id="element-5840" data-mp="1e8eckeqvtj68">
                    <div className="font-semibold text-slate-900 text-sm" data-id="element-5841" data-mp="1e7lgq8tgoe2v">
                      Nathalie Ouellet
                    </div>
                    <div className="text-sm text-slate-500 mt-0.5" data-id="element-5842" data-mp="1n7rqpkpyavxa">
                      (450) 658-1157
                    </div>
                    <div className="text-sm text-slate-500" data-id="element-5843" data-mp="1j5d5ityt7k2u">
                      adjointe.tp@ville.richelieu.qc.ca
                    </div>
                  </div>
                </div>
              </div>
              <div data-id="element-5844" data-mp="2uktgdvnrlhti">
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3" data-id="element-5845" data-mp="2ulbmdk08baem">
                  {t('EMPLACEMENT', 'LOCATION')}
                </h3>
                <div className="flex items-start gap-2" data-id="element-5846" data-mp="3tyeif2gjmeen">
                  <MapPinIcon className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" data-id="element-5847" data-mp="3tz6cg8uhs42t" />
                  <div data-id="element-5848" data-mp="2t4wpmzl0mau">
                    <div className="text-sm text-slate-700" data-id="element-5849" data-mp="7pvgakali010">
                      200 Boulevard Richelieu
                    </div>
                    <div className="text-sm text-slate-700" data-id="element-5850" data-mp="co6lghpy3mcw">
                      Richelieu, J3L 3R1
                    </div>
                    <button className="text-sm text-[#4F46E5] mt-1 hover:underline" data-id="element-5851" data-mp="gp0f7rq2jvdp">
                      {t('Voir sur la carte ▾', 'View on map ▾')}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div data-id="element-5852" data-mp="vgirbeft55rs">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3" data-id="element-5853" data-mp="vh0wj0kg6ubu">
                {t('PIÈCES JOINTES', 'ATTACHMENTS')}
              </h3>
              <div className="flex gap-3" data-id="element-5854" data-mp="2kxeap6gk1fhx">
                <div className="w-20 h-20 rounded-lg bg-slate-200 overflow-hidden" data-id="element-5855" data-mp="2ky65if2bv975">
                  <img src="https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=200&h=200&fit=crop" alt="Parc" className="w-full h-full object-cover" data-id="element-5856" data-mp="2fwyywjbx8g22" />
                </div>
                <div className="w-20 h-20 rounded-lg bg-slate-200 overflow-hidden" data-id="element-5857" data-mp="33nvycwmj5kwf">
                  <img src="https://images.unsplash.com/photo-1519331379826-f10be5486c6f?w=200&h=200&fit=crop" alt="Parc" className="w-full h-full object-cover" data-id="element-5858" data-mp="2yo9isgj8bskg" />
                </div>
                <button className="w-20 h-20 rounded-lg border-2 border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-400 hover:text-[#4F46E5] hover:border-[#4F46E5] transition-colors" data-id="element-5859" data-mp="2oxx0kuyw0bhh">
                  <PlusIcon className="w-5 h-5 mb-1" data-id="element-5860" data-mp="3hl13muef6fqm" />
                  <span className="text-xs font-medium" data-id="element-5861" data-mp="1fqmsy3iaugpe">{t('Téléverser', 'Upload')}</span>
                </button>
              </div>
            </div>
            </>}
          </div>
        </div>

        {/* Right Pane - Internal */}
        <div className="w-1/2 bg-slate-50 flex flex-col relative" data-id="element-5862" data-mp="15xxt65w42r9w">
          <button onClick={() => setSelectedRequest(null)} aria-label={t('Fermer', 'Close')} className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-200 transition-colors z-10" data-id="element-5863" data-mp="1axaf8of5rubf">
            <XIcon className="w-5 h-5" data-id="element-5864" data-mp="n1plywjjg6by" />
          </button>

          <div className="p-4 border-b border-slate-200 flex items-center justify-between text-slate-500 text-sm font-medium bg-white" data-id="element-5865" data-mp="n3946sor8rgd">
            <div className="flex items-center gap-2" data-id="element-5866" data-mp="2hensr6614kuj">
              <div className="w-4 h-4 rounded flex items-center justify-center border-2 border-slate-300" data-id="element-5867" data-mp="2hcuo1wez8ap6">
                <div className="w-2 h-2 bg-slate-300 rounded-sm" data-id="element-5868" data-mp="2mbnzljapdrmp"></div>
              </div>
              {t('Interne — non visible par le citoyen', 'Internal — not visible to the citizen')}
            </div>
            <PrinterIcon className="w-4 h-4 mr-10" data-id="element-5869" data-mp="kghvno8gk6yd" />
          </div>

          <div className="px-6 pt-4 border-b border-slate-200 flex gap-6 bg-white" data-id="element-5870" data-mp="khsz1mxzrcoy">
            {[{
            id: 'gestion' as const,
            label: t('Gestion', 'Management')
          }, {
            id: 'collaboration' as const,
            label: t('Collaboration', 'Collaboration')
          }, {
            id: 'historique' as const,
            label: t('Historique', 'History')
          }].map(tab => <button key={tab.id} onClick={() => setRightTab(tab.id)} className={`pb-3 border-b-2 font-semibold text-sm transition-colors ${rightTab === tab.id ? 'border-[#4F46E5] text-[#4F46E5]' : 'border-transparent text-slate-500 hover:text-slate-700'}`} data-id="element-5871" data-mp="13czvksxgnd5p">
                {tab.label}
              </button>)}
          </div>

          {rightTab === 'collaboration' && <div className="p-6 overflow-y-auto flex-1 space-y-6" data-id="element-5872" data-mp="2o3qbcobp81ib">
              <div className="bg-white rounded-xl border border-slate-200 p-5" data-id="element-5873" data-mp="2o6m9acnjqq6r">
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4" data-id="element-5874" data-mp="2t2jp8huwduj0">
                  {t('INTERVENANTS ASSIGNÉS', 'ASSIGNED STAFF')}
                </h3>
                <div className="space-y-3" data-id="element-5875" data-mp="20fgw658zqc05">
                  <div className="flex items-center justify-between" data-id="element-5876" data-mp="25ds4gbjn0eg9">
                    <div className="flex items-center gap-3" data-id="element-5877" data-mp="25cgzi8egh0nk">
                      <div className="w-8 h-8 rounded-full bg-slate-800 text-white flex items-center justify-center text-[10px] font-bold" data-id="element-5878" data-mp="29fm0g6iw0ua3">
                        JD
                      </div>
                      <div data-id="element-5879" data-mp="3ktr9vf1vdwct">
                        <div className="text-sm font-semibold text-slate-900" data-id="element-5880" data-mp="3ksxwvwcbiam8">
                          Jacques Dubois
                        </div>
                        <div className="text-xs text-slate-500" data-id="element-5881" data-mp="155u3pazvnosn">
                          {t('Travaux publics · Responsable', 'Public Works · Lead')}
                        </div>
                      </div>
                    </div>
                    <span className="text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-1 rounded border border-emerald-100" data-id="element-5882" data-mp="vepkfz365hs0">
                      {t('Actif', 'Active')}
                    </span>
                  </div>
                  <div className="flex items-center justify-between" data-id="element-5883" data-mp="lg9yeaj00ypd">
                    <div className="flex items-center gap-3" data-id="element-5884" data-mp="hgson4wjhfee">
                      <div className="w-8 h-8 rounded-full bg-amber-500 text-white flex items-center justify-center text-[10px] font-bold" data-id="element-5885" data-mp="ch6cyzwd077p">
                        MG
                      </div>
                      <div data-id="element-5886" data-mp="26v793d0iyg43">
                        <div className="text-sm font-semibold text-slate-900" data-id="element-5887" data-mp="26vz5ur5uyjvy">
                          Martin Gauthier
                        </div>
                        <div className="text-xs text-slate-500" data-id="element-5888" data-mp="2x2i903hmj428">
                          {t('Parcs et espaces verts · Exécutant', 'Parks and green spaces · Field crew')}
                        </div>
                      </div>
                    </div>
                    <span className="text-xs font-medium text-amber-700 bg-amber-50 px-2 py-1 rounded border border-amber-100" data-id="element-5889" data-mp="31wmj2vm6rs7j">
                      {t('Intervention prévue', 'Intervention scheduled')}
                    </span>
                  </div>
                  <div className="flex items-center justify-between" data-id="element-5890" data-mp="2n6nmgxaa4ou6">
                    <div className="flex items-center gap-3" data-id="element-5891" data-mp="2n7ypuvztbukr">
                      <div className="w-8 h-8 rounded-full bg-emerald-500 text-white flex items-center justify-center text-[10px] font-bold" data-id="element-5892" data-mp="2i7ayg97o9bq0">
                        ST
                      </div>
                      <div data-id="element-5893" data-mp="3tnit7ogv3ir4">
                        <div className="text-sm font-semibold text-slate-900" data-id="element-5894" data-mp="3tm7qlrz5kh1l">
                          Sylvie Tremblay
                        </div>
                        <div className="text-xs text-slate-500" data-id="element-5895" data-mp="9zpgh4oe01ct">
                          {t('Réception · Observatrice', 'Front desk · Observer')}
                        </div>
                      </div>
                    </div>
                    <button className="text-xs font-medium text-[#4F46E5] hover:text-[#4338CA]" data-id="element-5896" data-mp="10c5g7km74pz">
                      {t('Voir', 'View')}
                    </button>
                  </div>
                </div>
                <button className="mt-4 flex items-center gap-2 text-sm font-medium text-[#4F46E5] hover:text-[#4338CA]" data-id="element-5897" data-mp="tnexqhyp6j7s">
                  <PlusIcon className="w-4 h-4" data-id="element-5898" data-mp="ewxwozpz5vbf" /> {t('Ajouter un intervenant', 'Add a staff member')}
                </button>
              </div>

              <div className="bg-white rounded-xl border border-slate-200 p-5" data-id="element-5899" data-mp="17nfxtv751mm7">
                <div className="flex items-center justify-between gap-3 mb-4" data-id="element-5900" data-mp="1ce64rcv7pxj0">
                  <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-5901" data-mp="1cg95aqtzr91z">
                    {t('NOTES INTERNES', 'INTERNAL NOTES')}
                  </h3>
                  <span className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-400" data-id="element-5902" data-mp="1hc4qz6oz50b3">
                    <LockIcon className="w-3 h-3" data-id="element-5903" data-mp="1cebmqj78egdg" />
                    {t('Jamais visible par le citoyen', 'Never visible to the citizen')}
                  </span>
                </div>
                <div className="space-y-3" data-id="element-5904" data-mp="13d568a4l15k7">
                  <div className="flex gap-3" data-id="element-5905" data-mp="yl3tkn3i5ctn">
                    <div className="w-7 h-7 rounded-full bg-slate-800 text-white flex items-center justify-center text-[10px] font-bold flex-shrink-0" data-id="element-5906" data-mp="yj99tc3yvqr5">
                      JD
                    </div>
                    <div className="flex-1" data-id="element-5907" data-mp="oo8yyfacnwkn">
                      <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-700 border border-slate-200" data-id="element-5908" data-mp="kkt1jgqghs6w">
                        {t("Visite sur place effectuée. Trois branches de bonne taille à tronçonner, la nacelle n'est pas nécessaire. Prévoir deux employés pour l'avant-midi.", 'Site visit done. Three large branches to cut up, the bucket truck is not needed. Plan two workers for the morning.')}
                      </div>
                      <div className="text-[11px] text-slate-400 mt-1" data-id="element-5909" data-mp="359ck26c3x6oo">
                        Jacques Dubois · {t('il y a 2 h', '2 h ago')}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-3" data-id="element-5910" data-mp="2cl8dlilpopkx">
                    <div className="w-7 h-7 rounded-full bg-amber-500 text-white flex items-center justify-center text-[10px] font-bold flex-shrink-0" data-id="element-5911" data-mp="2hji95ubcls72">
                      MG
                    </div>
                    <div className="flex-1" data-id="element-5912" data-mp="1ouugves6m2k0">
                      <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-700 border border-slate-200" data-id="element-5913" data-mp="1ou2xr3frtxam">
                        {t("Le camion nacelle est en réparation jusqu'au 22. On peut quand même y aller au sol comme prévu le 21.", 'The bucket truck is under repair until the 22nd. We can still work from the ground as planned on the 21st.')}
                      </div>
                      <div className="text-[11px] text-slate-400 mt-1" data-id="element-5914" data-mp="4d24hcqz694h">
                        Martin Gauthier · {t('hier, 16:04', 'yesterday, 16:04')}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-4 flex items-center gap-2" data-id="element-5915" data-mp="e23hqv64ycer">
                  <input type="text" value={noteDraft} onChange={e => setNoteDraft(e.target.value)} placeholder={t('Ajouter une note interne...', 'Add an internal note...')} className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:border-transparent" data-id="element-5916" data-mp="s2o2hwvm2iaw" />
                  <button disabled={!noteDraft.trim()} onClick={() => setNoteDraft('')} className="px-4 py-2 bg-[#0F172A] text-white text-sm font-semibold rounded-lg hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors" data-id="element-5917" data-mp="2fp0s2ic1ztdu">
                    {t('Publier', 'Post')}
                  </button>
                </div>
              </div>
            </div>}

          {rightTab === 'historique' && <div className="p-6 overflow-y-auto flex-1" data-id="element-5918" data-mp="20wypurmigoan">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4" data-id="element-5919" data-mp="1n0v8hywqkafl">
                {t("JOURNAL D'ACTIVITÉ", 'ACTIVITY LOG')}
              </h3>
              <div className="relative border-l-2 border-slate-200 ml-3 space-y-6" data-id="element-5920" data-mp="3hd1nl8sr3bd6">
                <div className="relative pl-6" data-id="element-5921" data-mp="3mce7pls8m4c2">
                  <div className="absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-[#4F46E5] border-2 border-white" data-id="element-5922" data-mp="3mc5u1roaiuzt"></div>
                  <div className="text-sm font-semibold text-slate-900" data-id="element-5923" data-mp="3jvwlfa0g5hzg">
                    {t('Intervention planifiée au 21 mai', 'Intervention scheduled for May 21')}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5" data-id="element-5924" data-mp="3azf8q2cfpc1l">
                    Martin Gauthier ({t('Parcs et espaces verts', 'Parks and green spaces')})
                  </div>
                  <div className="text-xs text-slate-400 mt-0.5" data-id="element-5925" data-mp="7g2sth6ho7ei">
                    {t('20 mai 2026 · 16:04', 'May 20, 2026 · 16:04')}
                  </div>
                </div>
                <div className="relative pl-6" data-id="element-5926" data-mp="3tuvwez8opppm">
                  <div className="absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-[#4F46E5] border-2 border-white" data-id="element-5927" data-mp="3ox3t792lah64"></div>
                  <div className="text-sm font-semibold text-slate-900" data-id="element-5928" data-mp="q7ltvigydbdw">
                    {t('Réponse envoyée au citoyen', 'Reply sent to the citizen')}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5" data-id="element-5929" data-mp="srjkjx5j7uiq">
                    Jacques Dubois ({t('Travaux publics', 'Public Works')})
                  </div>
                  <div className="text-xs text-slate-400 mt-0.5" data-id="element-5930" data-mp="xqv3nctdfc3h">
                    {t('19 mai 2026 · 09:47', 'May 19, 2026 · 09:47')}
                  </div>
                </div>
                <div className="relative pl-6" data-id="element-5931" data-mp="17pk92642z42u">
                  <div className="absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-amber-500 border-2 border-white" data-id="element-5932" data-mp="1bqdwyzhiwivq"></div>
                  <div className="text-sm font-semibold text-slate-900" data-id="element-5933" data-mp="1gop7zdjk0zfj">
                    {t('Statut changé de « Reçue » à « En cours »', 'Status changed from “Received” to “In progress”')}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5" data-id="element-5934" data-mp="1qe8tys2kd9em">
                    Jacques Dubois ({t('Travaux publics', 'Public Works')})
                  </div>
                  <div className="text-xs text-slate-400 mt-0.5" data-id="element-5935" data-mp="1zfp0goq9crdk">
                    {t('19 mai 2026 · 09:30', 'May 19, 2026 · 09:30')}
                  </div>
                </div>
                <div className="relative pl-6" data-id="element-5936" data-mp="2ksrl8xb6c6qx">
                  <div className="absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-emerald-500 border-2 border-white" data-id="element-5937" data-mp="2goa8hi38etpn"></div>
                  <div className="text-sm font-semibold text-slate-900" data-id="element-5938" data-mp="2uobflfa3axdk">
                    {t('Requête assignée aux Travaux publics', 'Request assigned to Public Works')}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5" data-id="element-5939" data-mp="220p1hd38kacg">
                    Sylvie Tremblay ({t('Réception', 'Front desk')})
                  </div>
                  <div className="text-xs text-slate-400 mt-0.5" data-id="element-5940" data-mp="2bqicoczgung6">
                    {t('19 mai 2026 · 08:41', 'May 19, 2026 · 08:41')}
                  </div>
                </div>
                <div className="relative pl-6" data-id="element-5941" data-mp="1j9o7l3gz34zr">
                  <div className="absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-slate-300 border-2 border-white" data-id="element-5942" data-mp="1j8d3f2jm7v84"></div>
                  <div className="text-sm font-semibold text-slate-900" data-id="element-5943" data-mp="1e90gkhsquo5j">
                    {t('Requête déposée avec deux photos', 'Request submitted with two photos')}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5" data-id="element-5944" data-mp="3uqzssy63ceck">
                    Nathalie Ouellet ({t('Application bciti+', 'bciti+ app')})
                  </div>
                  <div className="text-xs text-slate-400 mt-0.5" data-id="element-5945" data-mp="3kzwsvzowe5ev">
                    {t('19 mai 2026 · 08:12', 'May 19, 2026 · 08:12')}
                  </div>
                </div>
              </div>
            </div>}

          <div className={`p-6 overflow-y-auto flex-1 space-y-6 ${rightTab === 'gestion' ? '' : 'hidden'}`} data-id="element-5946" data-mp="hgafz6zhptcl">
            <div className="text-sm text-slate-500 font-medium" data-id="element-5947" data-mp="8h6yg7fz1m07">
              {t('19 mai', 'May 19')} <span className="mx-2 text-slate-300" data-id="element-5948" data-mp="2o94a2uakxz9e">→</span>{' '}
              <span className="text-red-500" data-id="element-5949" data-mp="2o7jhha7tsqe6">{t('26 mai', 'May 26')}</span>
            </div>

            {/* Tasks */}
            <div className="space-y-3" data-id="element-5950" data-mp="2t5l0jv594ppn">
              <div className="flex items-start gap-3" data-id="element-5951" data-mp="2d061o9dswfkd">
                <CheckSquareIcon className="w-5 h-5 text-slate-900 mt-0.5" data-id="element-5952" data-mp="2czctr533vk0n" />
                <div className="flex-1" data-id="element-5953" data-mp="2ht8lvqwoucnw">
                  <div className="text-sm text-slate-500 line-through" data-id="element-5954" data-mp="2339mjktecv6u">
                    {t('Vérifier la disponibilité des équipes', 'Check crew availability')}
                  </div>
                  <div className="text-xs text-slate-400 mt-0.5" data-id="element-5955" data-mp="2vrp002wvr5g2">
                    {t('Terminée : 22 mai 2026', 'Completed: May 22, 2026')}{' '}
                    <PencilIcon className="w-3 h-3 inline ml-1" data-id="element-5956" data-mp="30pgqqtlsglio" />
                  </div>
                </div>
                <div className="text-xs text-red-500 flex items-center gap-1" data-id="element-5957" data-mp="2lszmsnfxi965">
                  <CalendarIcon className="w-3 h-3" data-id="element-5958" data-mp="2qrat4o70m1jm" /> {t('22 mai 2026', 'May 22, 2026')}
                </div>
                <div className="w-6 h-6 rounded-full bg-indigo-500 text-white flex items-center justify-center text-[10px] font-bold" data-id="element-5959" data-mp="1acsr3zytt3gj">
                  ML
                </div>
              </div>
              <div className="flex items-start gap-3" data-id="element-5960" data-mp="3tjwwud6uhnrh">
                <SquareIcon className="w-5 h-5 text-slate-300 mt-0.5" data-id="element-5961" data-mp="3omddwzz7cmt7" />
                <div className="flex-1" data-id="element-5962" data-mp="3oo7trzvm9oqf">
                  <div className="text-sm text-slate-700" data-id="element-5963" data-mp="6gs19mh4ia5k">
                    {t('Planifier le ramassage des branches', 'Schedule branch pickup')}
                  </div>
                </div>
                <div className="text-xs text-red-500 flex items-center gap-1" data-id="element-5964" data-mp="3au70vmrcivxz">
                  <CalendarIcon className="w-3 h-3" data-id="element-5965" data-mp="35vm1fpjx4gal" /> {t('25 mai 2026', 'May 25, 2026')}
                </div>
                <div className="w-6 h-6 rounded-full bg-[#4F46E5] text-white flex items-center justify-center text-[10px] font-bold" data-id="element-5966" data-mp="3jms7k4hpdc6g">
                  ST
                </div>
              </div>
              <div className="flex items-start gap-3" data-id="element-5967" data-mp="2r6hho3xh640d">
                <SquareIcon className="w-5 h-5 text-slate-300 mt-0.5" data-id="element-5968" data-mp="2r4wlk9viid0e" />
                <div className="flex-1" data-id="element-5969" data-mp="2n42ytjtt3493">
                  <div className="text-sm text-slate-700" data-id="element-5970" data-mp="2n31hwyq3znjg">
                    {t('Contacter le demandeur pour suivi', 'Contact the requester for follow-up')}
                  </div>
                </div>
                <div className="text-xs text-red-500 flex items-center gap-1" data-id="element-5971" data-mp="v18b1a70n2ar">
                  <CalendarIcon className="w-3 h-3" data-id="element-5972" data-mp="zv32x0wrynkx" /> {t('20 mai 2026', 'May 20, 2026')}
                </div>
                <div className="w-6 h-6 rounded-full border border-dashed border-slate-300 text-slate-400 flex items-center justify-center text-[10px]" data-id="element-5973" data-mp="14vh01nwu8d46">
                  <UserIcon className="w-3 h-3" data-id="element-5974" data-mp="c5rzfbo1if33" />
                </div>
              </div>
              <button className="text-sm text-slate-500 font-medium flex items-center gap-1 hover:text-slate-700 mt-2" data-id="element-5975" data-mp="h3k290q83lm2">
                <PlusIcon className="w-4 h-4" data-id="element-5976" data-mp="m3o9s195l605" /> {t('Ajouter une tâche', 'Add a task')}
              </button>
            </div>

            {/* Linked Requests */}
            <div className="bg-white rounded-xl border border-slate-200 p-5" data-id="element-5977" data-mp="1xhtg30wu9ryn">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4" data-id="element-5978" data-mp="1xjwg8drph1h3">
                {t('REQUÊTES LIÉES', 'LINKED REQUESTS')}
              </h3>
              <div className="space-y-3" data-id="element-5979" data-mp="3d1j59btodukc">
                <div className="flex items-center justify-between p-2 hover:bg-slate-50 rounded-lg border border-transparent hover:border-slate-100 transition-colors" data-id="element-5980" data-mp="38a168tb7q420">
                  <div className="flex items-center gap-3" data-id="element-5981" data-mp="3393r6pli1q4q">
                    <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2 py-1 rounded border border-slate-200" data-id="element-5982" data-mp="2yd684biuq5o9">
                      {t('Liée ▾', 'Related ▾')}
                    </span>
                    <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2 py-1 rounded border border-slate-200" data-id="element-5983" data-mp="2pc83br0l9u4m">
                      R-68 ↗
                    </span>
                    <span className="text-sm text-slate-700" data-id="element-5984" data-mp="nkbu4o1w8wd0">
                      {tr('Lampadaire défectueux')}
                    </span>
                  </div>
                  <div className={`px-2 py-0.5 rounded-full text-xs font-medium flex items-center gap-1.5 ${getStatusColor('En Attente')}`} data-id="element-5985" data-mp="3ccywgkh143rh">
                    <div className={`w-1.5 h-1.5 rounded-full ${getStatusDot('En Attente')}`} data-id="element-5986" data-mp="3gcg1jcsjmyw4"></div>
                    {tr('En Attente')}
                  </div>
                </div>
                <div className="flex items-center justify-between p-2 hover:bg-slate-50 rounded-lg border border-transparent hover:border-slate-100 transition-colors" data-id="element-5987" data-mp="2sp7xszarjxw7">
                  <div className="flex items-center gap-3" data-id="element-5988" data-mp="2xhsovt2abww1">
                    <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2 py-1 rounded border border-slate-200" data-id="element-5989" data-mp="2jpchyh69jepu">
                      {t('Bloque ▾', 'Blocks ▾')}
                    </span>
                    <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2 py-1 rounded border border-slate-200" data-id="element-5990" data-mp="29r562cqtzmup">
                      R-72 ↗
                    </span>
                    <span className="text-sm text-slate-700" data-id="element-5991" data-mp="1v2mycr6bihe7">
                      {t('Nid-de-poule rue Principale', 'Pothole on Principale Street')}
                    </span>
                  </div>
                  <div className={`px-2 py-0.5 rounded-full text-xs font-medium flex items-center gap-1.5 ${getStatusColor('Reçue')}`} data-id="element-5992" data-mp="251uji0jangbv">
                    <div className={`w-1.5 h-1.5 rounded-full ${getStatusDot('Reçue')}`} data-id="element-5993" data-mp="2e20wllb2yeai"></div>
                    {tr('Reçue')}
                  </div>
                </div>
                <div className="flex items-center justify-between p-2 hover:bg-slate-50 rounded-lg border border-transparent hover:border-slate-100 transition-colors" data-id="element-5994" data-mp="2nsbzd4j12hft">
                  <div className="flex items-center gap-3" data-id="element-5995" data-mp="2xq9lnmhbu45s">
                    <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2 py-1 rounded border border-slate-200" data-id="element-5996" data-mp="2xqrq36e57oos">
                      {t('Doublon ▾', 'Duplicate ▾')}
                    </span>
                    <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2 py-1 rounded border border-slate-200" data-id="element-5997" data-mp="2zw6hr4ge8ouj">
                      R-75 ↗
                    </span>
                    <span className="text-sm text-slate-700" data-id="element-5998" data-mp="39qygiblpphqw">
                      {t('Banc de parc endommagé', 'Damaged park bench')}
                    </span>
                  </div>
                  <div className={`px-2 py-0.5 rounded-full text-xs font-medium flex items-center gap-1.5 ${getStatusColor('En Cours')}`} data-id="element-5999" data-mp="3jhb123tj1ax1">
                    <div className={`w-1.5 h-1.5 rounded-full ${getStatusDot('En Cours')}`} data-id="element-6000" data-mp="3siiyyks4s9ql"></div>
                    {tr('En Cours')}
                  </div>
                </div>
              </div>
              <button className="text-sm text-slate-500 font-medium flex items-center gap-1 hover:text-slate-700 mt-3" data-id="element-6001" data-mp="1y6kpqittndup">
                <PlusIcon className="w-4 h-4" data-id="element-6002" data-mp="235xkrquujr8d" /> {t('Lier une requête', 'Link a request')}
              </button>
            </div>

            {/* Tags */}
            <div className="bg-white rounded-xl border border-slate-200 p-5" data-id="element-6003" data-mp="1qsts2vandfkr">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4" data-id="element-6004" data-mp="1lu8t0z74t1xw">
                {t('ÉTIQUETTES', 'TAGS')}
              </h3>
              <div className="flex flex-wrap gap-2" data-id="element-6005" data-mp="1gtks9lt3p6qh">
                <span className="px-3 py-1 rounded-full bg-indigo-50 text-indigo-600 text-xs font-medium border border-indigo-100 flex items-center gap-1" data-id="element-6006" data-mp="2ejpefcwc3ikm">
                  {t('Urgent', 'Urgent')} <XIcon className="w-3 h-3 cursor-pointer" data-id="element-6007" data-mp="24kqimuwo3hf1" />
                </span>
                <span className="px-3 py-1 rounded-full bg-indigo-50 text-indigo-600 text-xs font-medium border border-indigo-100 flex items-center gap-1" data-id="element-6008" data-mp="1zm6tymqovriz">
                  {t('Suivi requis', 'Follow-up needed')} <XIcon className="w-3 h-3 cursor-pointer" data-id="element-6009" data-mp="o7004j4swqlv" />
                </span>
                <button className="px-3 py-1 rounded-full border border-dashed border-slate-300 text-slate-500 text-xs font-medium flex items-center gap-1 hover:border-slate-400 hover:text-slate-700 transition-colors" data-id="element-6010" data-mp="o94bfm514rw6">
                  <PlusIcon className="w-3 h-3" data-id="element-6011" data-mp="14uh4qusuemqz" /> {t('Ajouter une étiquette', 'Add a tag')}
                </button>
              </div>
            </div>

            {/* Internal Files */}
            <div className="bg-white rounded-xl border border-slate-200 p-5" data-id="element-6012" data-mp="18xvre4bae1de">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4" data-id="element-6013" data-mp="1wmezojuc6e5h">
                {t('FICHIERS INTERNES', 'INTERNAL FILES')}
              </h3>
              <div className="flex gap-3" data-id="element-6014" data-mp="1nsjw3g8v4vry">
                <div className="w-20 rounded-lg border border-slate-200 p-2 flex flex-col items-center justify-center text-center" data-id="element-6015" data-mp="1nriib40gpv6j">
                  <FileTextIcon className="w-8 h-8 text-red-500 mb-2" data-id="element-6016" data-mp="2fk97wvnn4lmt" />
                  <div className="text-[10px] font-medium text-slate-700 truncate w-full" data-id="element-6017" data-mp="2kb7ozxggdnr4">
                    rapport_inspe...
                  </div>
                  <div className="text-[9px] text-slate-400" data-id="element-6018" data-mp="8uxq53dswnbc">{t('22 mai', 'May 22')}</div>
                </div>
                <div className="w-20 h-24 rounded-lg bg-slate-200 overflow-hidden" data-id="element-6019" data-mp="3wmfiqfom8s2">
                  <img src="https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=200&h=200&fit=crop" alt="Doc" className="w-full h-full object-cover" data-id="element-6020" data-mp="ikqsrevyts3g" />
                </div>
                <div className="w-20 h-24 rounded-lg bg-slate-200 overflow-hidden" data-id="element-6021" data-mp="3h3xp775sogjj">
                  <img src="https://images.unsplash.com/photo-1518495973542-4542c06a5843?w=200&h=200&fit=crop" alt="Doc" className="w-full h-full object-cover" data-id="element-6022" data-mp="3w0yhbbrwedij" />
                </div>
                <button className="w-20 h-24 rounded-lg border-2 border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-400 hover:text-[#4F46E5] hover:border-[#4F46E5] transition-colors" data-id="element-6023" data-mp="1b965tvvje0c5">
                  <PlusIcon className="w-5 h-5 mb-1" data-id="element-6024" data-mp="1kw4h5da5vw9s" />
                  <span className="text-xs font-medium" data-id="element-6025" data-mp="1fyuta22f7so2">{t('Téléverser', 'Upload')}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>;
  }
  return <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden" data-id="element-6026" data-mp="16zhi94ynew18">
      {/* Header */}
      <div className="p-4 border-b border-slate-200" data-id="element-6027" data-mp="23o73n59hi6ry">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4" data-id="element-6028" data-mp="1ypvpwjg2jc4g">
          <div className="flex items-center gap-3" data-id="element-6029" data-mp="1tynl60pooexm">
            <div className="inline-flex items-center bg-slate-100 rounded-lg p-1 gap-1" data-id="element-6030" data-mp="1txcd3op7gl0p">
              <button onClick={() => setViewMode('list')} className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-semibold transition-colors ${viewMode === 'list' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-6031" data-mp="1pwiosu7up47a">
                <TableIcon className="w-4 h-4" data-id="element-6032" data-mp="47evatcqfs5t" />
                {t('Tableau', 'Table')}
              </button>
              <button onClick={() => setViewMode('map')} className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-semibold transition-colors ${viewMode === 'map' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-6033" data-mp="9714mrpfwo9c">
                <MapIcon className="w-4 h-4" data-id="element-6034" data-mp="n2llzipisyen" />
                {t('Carte', 'Map')}
              </button>
            </div>
            <span className="text-sm text-slate-500" data-id="element-6035" data-mp="wurfwxzc1pwh">{t('10 demandes', '10 requests')}</span>
          </div>

          <div className="flex items-center gap-3" data-id="element-6036" data-mp="10wmfny1m7v85">
            <div className="relative" data-id="element-6037" data-mp="10vb95qgsc9dc">
              <SearchIcon className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" data-id="element-6038" data-mp="15vfubtsn1s9w" />
              <input type="text" placeholder={t('Rechercher des requêtes...', 'Search requests...')} className="pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-sm w-64 focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:border-transparent" data-id="element-6039" data-mp="1atr301775wqj" />
            </div>
            <button className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 text-slate-700 text-sm font-semibold rounded-lg hover:bg-slate-50 transition-colors" data-id="element-6040" data-mp="3go05i5gv3dq4">
              <FilterIcon className="w-4 h-4" data-id="element-6041" data-mp="3pmvgduuupbzr" />
              {t('Filtrer', 'Filter')}
            </button>
            <button onClick={() => setShowCreate(true)} className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white text-sm font-semibold rounded-lg hover:bg-slate-800 transition-colors whitespace-nowrap" data-id="element-6042" data-mp="3lljkx8papwjn">
              <PlusIcon className="w-4 h-4" data-id="element-6043" data-mp="257tg1zqcr48y" />
              {t('Créer une requête', 'Create a request')}
            </button>
          </div>
        </div>
      </div>

      {/* Map View */}
      {viewMode === 'map' && <div className="h-[400px] w-full border-b border-slate-200" data-id="element-6044" data-mp="fqo3ycd6e99f">
          <MapContainer center={[45.4499, -73.4653]} zoom={13} scrollWheelZoom={false} style={{
        height: '100%',
        width: '100%'
      }} data-id="element-6045" data-mp="kq11ps5l50qs">
            <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" data-id="element-6046" data-mp="azftre40ujv7" />
            {MAP_MARKERS.map(m => <Marker key={m.reqId} position={m.position} icon={requestMarkerIcon} data-id="element-6047" data-mp="1xgd4mwx5hss">
                <Popup data-id="element-6048" data-mp="3o5y4vh5bx8fm">
                  <div className="text-sm" data-id="element-6049" data-mp="3o79hm6gr5qiv">
                    <div className="font-semibold text-slate-900" data-id="element-6050" data-mp="1hg9yp5r4tycy">
                      {m.reqId} — {tr(m.subject)}
                    </div>
                    <div className="text-slate-500" data-id="element-6051" data-mp="1me3785fwr4ge">{tr(m.category)}</div>
                  </div>
                </Popup>
              </Marker>)}
          </MapContainer>
        </div>}

      {/* Table */}
      <div className="overflow-x-auto" data-id="element-6052" data-mp="3ehpamgfwgtjd">
        <table className="w-full text-left border-collapse" data-id="element-6053" data-mp="39hkuiriwm0to">
          <thead data-id="element-6054" data-mp="39g02b8k2atyz">
            <tr className="border-b border-slate-200 bg-white" data-id="element-6055" data-mp="39jnr5uekas7d">
              <th className="px-4 py-3 text-xs font-semibold text-slate-500 whitespace-nowrap" data-id="element-6056" data-mp="34i86w4k7kpq1">
                <span className="inline-flex items-center gap-1" data-id="element-6057" data-mp="2zl81zwz3ht0i">
                  {t('Sujet', 'Subject')}{' '}
                  <ChevronsUpDownIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-6058" data-mp="2urn9gkg9tr8p" />
                </span>
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-500 whitespace-nowrap" data-id="element-6059" data-mp="2qoz5aq2i58i2">
                <span className="inline-flex items-center gap-1" data-id="element-6060" data-mp="2lqffgeksfckf">
                  {t('Catégorie', 'Category')}{' '}
                  <ChevronsUpDownIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-6061" data-mp="2bucgfhl8fu1p" />
                </span>
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-500 whitespace-nowrap" data-id="element-6062" data-mp="21qxagpd18te1">
                <span className="inline-flex items-center gap-1" data-id="element-6063" data-mp="26oflclam1ioo">
                  {t('Département', 'Department')}{' '}
                  <ChevronsUpDownIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-6064" data-mp="2bpmryh10obtr" />
                </span>
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-500 whitespace-nowrap" data-id="element-6065" data-mp="2gmmyet1s3gle">
                <span className="inline-flex items-center gap-1" data-id="element-6066" data-mp="1j1h8rypm2uw1">
                  {t('ID Requête', 'Request ID')}{' '}
                  <ChevronsUpDownIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-6067" data-mp="1nwejda2jhcb1" />
                </span>
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-500 whitespace-nowrap" data-id="element-6068" data-mp="1xoai8ncg96gb">
                {t('Emplacement', 'Location')}
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-500 whitespace-nowrap" data-id="element-6069" data-mp="1072a6a77p48q">
                <span className="inline-flex items-center gap-1" data-id="element-6070" data-mp="15653ixdtxm0v">
                  {t('Date de création', 'Created on')}{' '}
                  <ChevronsUpDownIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-6071" data-mp="s8gvuo4crlfr" />
                </span>
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-500 whitespace-nowrap" data-id="element-6072" data-mp="16xwjhv8l7ar8">
                <span className="inline-flex items-center gap-1" data-id="element-6073" data-mp="11xqn61rdh02t">
                  {t('Statut', 'Status')}{' '}
                  <ChevronsUpDownIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-6074" data-mp="e9qw10q5f7np" />
                </span>
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-500 whitespace-nowrap" data-id="element-6075" data-mp="9b5t2tjii9vk">
                {t('Demandeur', 'Requester')}
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-500 whitespace-nowrap" data-id="element-6076" data-mp="j795cs82vow8">
                {t('Date SLA', 'SLA date')}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-id="element-6077" data-mp="1un7j39e7az3s">
            {MOCK_REQUESTS.map(req => <tr key={req.id} onClick={() => setSelectedRequest(req)} className="hover:bg-slate-50 cursor-pointer transition-colors" data-id="element-6078" data-mp="226qdebswaero">
                <td className="px-4 py-3 text-sm text-slate-700 whitespace-nowrap" data-id="element-6079" data-mp="1t61ylevogadq">
                  <div className="flex items-center gap-2" data-id="element-6080" data-mp="1x7dq5py435ok">
                    <div className="w-8 h-8 rounded bg-slate-50 border border-slate-200 flex items-center justify-center" data-id="element-6081" data-mp="1x7vrh0zmsa3c">
                      <FolderIcon className="w-4 h-4 text-[#4F46E5]" data-id="element-6082" data-mp="2kzap4gggq389" />
                    </div>
                    {tr(req.subject)}
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap" data-id="element-6083" data-mp="2c4e66w6xxc77">
                  {tr(req.category)}
                </td>
                <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap" data-id="element-6084" data-mp="33w2gld65rone">
                  {tr(req.department)}
                </td>
                <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap" data-id="element-6085" data-mp="38nc0ry8xtzrw">
                  {req.reqId}
                </td>
                <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap" data-id="element-6086" data-mp="2jxpnp23fstsk">
                  <div className="flex items-center gap-1" data-id="element-6087" data-mp="2f338soy5cj3q">
                    {req.location}
                    <MapPinIcon className="w-3 h-3 text-[#4F46E5]" data-id="element-6088" data-mp="2a6cti3u61rjd" />
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap" data-id="element-6089" data-mp="2555lq4s0wscp">
                  {tr(req.date)}
                </td>
                <td className="px-4 py-3 whitespace-nowrap" data-id="element-6090" data-mp="2xv4d46tox1kj">
                  <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(req.status)}`} data-id="element-6091" data-mp="2xttd8i3d8dyp">
                    <div className={`w-1.5 h-1.5 rounded-full ${getStatusDot(req.status)}`} data-id="element-6092" data-mp="2nx835ichoco0"></div>
                    {tr(req.status)}
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap" data-id="element-6093" data-mp="3o5oe6jfqxh2j">
                  <div className="flex items-center gap-2" data-id="element-6094" data-mp="3o3lgdd8cqjn9">
                    <div className={`w-6 h-6 rounded-full ${req.requester.color} text-white flex items-center justify-center text-[10px] font-bold`} data-id="element-6095" data-mp="3t5ssq3x3g1q4">
                      {req.requester.initials}
                    </div>
                    {req.requester.name}
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap" data-id="element-6096" data-mp="fpuww2dlc1kf">
                  {tr(req.slaDate)}
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="p-4 border-t border-slate-200 flex items-center justify-end gap-6 text-sm text-slate-500" data-id="element-6097" data-mp="pmzkm6n3453c">
        <div className="flex items-center gap-2" data-id="element-6098" data-mp="2mhm741prz5at">
          {t('Éléments par page', 'Items per page')}
          <select className="border-b border-slate-300 pb-1 focus:outline-none bg-transparent font-medium text-slate-700" data-id="element-6099" data-mp="2rfxiwhzmrpvo">
            <option data-id="element-6100" data-mp="zbysginp23k8">10</option>
            <option data-id="element-6101" data-mp="14cmqvpe95moh">20</option>
            <option data-id="element-6102" data-mp="14dxthlvyooe0">50</option>
          </select>
        </div>
        <div data-id="element-6103" data-mp="18eztw61m76hk">1 - 10 / 98</div>
        <div className="flex items-center gap-4" data-id="element-6104" data-mp="18doq46865yqg">
          <button className="hover:text-slate-900" aria-label={t('Page précédente', 'Previous page')} data-id="element-6105" data-mp="18cwyt7llun5z">
            <ChevronLeftIcon className="w-4 h-4" data-id="element-6106" data-mp="1i4jgnmsg7khp" />
          </button>
          <button className="hover:text-slate-900" aria-label={t('Page suivante', 'Next page')} data-id="element-6107" data-mp="1i4b27qgog74e">
            <ChevronRightIcon className="w-4 h-4" data-id="element-6108" data-mp="1n1t1sq8t19zq" />
          </button>
        </div>
      </div>

      {showCreate && <CreateRequeteDialog onClose={() => setShowCreate(false)} data-id="element-6109" data-mp="1s0w80e0itm9a" />}
    </div>;
}
// Missing icons for the component
function CalendarIcon(props: React.SVGProps<SVGSVGElement>) {
  return <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-6110" data-mp="3twgbwjgedch7">
      <rect width="18" height="18" x="3" y="4" rx="2" ry="2" data-id="element-6111" data-mp="3lko3ddwaytue" />
      <line x1="16" x2="16" y1="2" y2="6" data-id="element-6112" data-mp="3gm3c43z13ki3" />
      <line x1="8" x2="8" y1="2" y2="6" data-id="element-6113" data-mp="3gmwbgjvnr7q7" />
      <line x1="3" x2="21" y1="10" y2="10" data-id="element-6114" data-mp="3ckrmvzk2h1af" />
    </svg>;
}
function UserIcon(props: React.SVGProps<SVGSVGElement>) {
  return <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-6115" data-mp="d5wcc8jqi7el">
      <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" data-id="element-6116" data-mp="46sraz10bhxg" />
      <circle cx="12" cy="7" r="4" data-id="element-6117" data-mp="3vahekdsj96y4" />
    </svg>;
}
function PencilIcon(props: React.SVGProps<SVGSVGElement>) {
  return <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-6118" data-mp="2ot409ttu8cdy">
      <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" data-id="element-6119" data-mp="2qbb1o9g26htn" />
      <path d="m15 5 4 4" data-id="element-6120" data-mp="2ccuwwo1rb1e5" />
    </svg>;
}